/**
 * Variable Replacer for Template Builder
 *
 * Replaces template variables with actual data
 * Supports:
 * - Simple variables: {{empresa_nome}}
 * - Group variables: {{grupo[N].propriedade}} where N is the position (1-indexed)
 *   in the available grupos array (filtered by radar=true, sorted by ordemQuestionario)
 *   Example: {{grupo[1]}} = first available group, {{grupo[2]}} = second available group
 * - Component variables: {{radar}}, {{quebra_pagina}}
 * - Image variables: {{imagem.nome}} (replaced with image URL)
 */

import { extractVariables, isImageVariable, extractImageName } from "../constants/template-variables";
import type { ReportData } from "../types";
import type { TemplateImage } from "../types/template";

/**
 * Variable replacement result
 */
export interface VariableReplacementResult {
  /** HTML with variables replaced */
  html: string;
  /** Variables that were not found/replaced */
  missingVariables: string[];
  /** Total number of replacements made */
  replacementCount: number;
}

/**
 * Options for variable replacement
 */
export interface ReplaceVariablesOptions {
  /** Template images for image variable replacement */
  images?: TemplateImage[];
}

/**
 * Replace all variables in template with actual data
 * @param template - HTML template with variables
 * @param data - Report data
 * @param options - Additional options (images, etc.)
 * @returns Replacement result
 */
export function replaceVariables(
  template: string,
  data: ReportData,
  options: ReplaceVariablesOptions = {}
): VariableReplacementResult {
  let html = template;
  const missingVariables: string[] = [];
  let replacementCount = 0;

  const { images = [] } = options;

  // Extract all variables from template
  const variables = [...new Set(extractVariables(template))];

  for (const variable of variables) {
    // Check if it's an image variable (formato: imagem.nome)
    if (isImageVariable(variable)) {
      const imageName = extractImageName(variable);
      const image = imageName ? images.find((img) => img.nome === imageName) : null;

      if (image) {
        const variablePattern = buildVariablePattern(variable);
        const matches = html.match(variablePattern);
        if (matches && matches.length > 0) {
          html = html.replace(variablePattern, image.url);
          replacementCount += matches.length;
        }
      } else {
        const availableImages = images.map((img) => img.nome).join(", ");
        if (availableImages) {
          missingVariables.push(`${variable} (imagem não encontrada - disponíveis: ${availableImages})`);
        } else {
          missingVariables.push(`${variable} (nenhuma imagem disponível no template)`);
        }
      }
      continue;
    }

    // Check if it's a grupo variable (formato: grupo[N].propriedade, N >= 1)
    const grupoMatch = variable.match(/^grupo\[(\d+)]\.([_a-z]+)$/);

    if (grupoMatch) {
      const ordemString = grupoMatch[1];
      const property = grupoMatch[2];

      // Validate captured groups exist
      if (!ordemString || !property) {
        missingVariables.push(variable);
        continue;
      }

      const ordem = Number.parseInt(ordemString, 10);

      // Validate: ordem must be >= 1 (1-indexed)
      if (Number.isNaN(ordem) || ordem < 1) {
        // Provide helpful error message for 0-index mistake
        if (ordem === 0) {
          missingVariables.push(
            `${variable} (índice inválido: use grupo[1] em vez de grupo[0] - a numeração começa em 1)`
          );
        } else {
          missingVariables.push(`${variable} (índice inválido)`);
        }
        continue;
      }

      const replacement = replaceGrupoVariable(ordem, property, data);
      const variablePattern = buildVariablePattern(variable);

      if (replacement === null) {
        // Provide helpful error message with valid index range
        const totalGrupos = data.grupos.length;
        if (totalGrupos === 0) {
          missingVariables.push(`${variable} (nenhum grupo disponível)`);
        } else if (ordem > totalGrupos) {
          missingVariables.push(`${variable} (grupo[${ordem}] não existe - índices válidos: 1 a ${totalGrupos})`);
        } else {
          missingVariables.push(`${variable} (propriedade inválida)`);
        }
      } else {
        const matches = html.match(variablePattern);
        if (matches && matches.length > 0) {
          html = html.replace(variablePattern, replacement);
          replacementCount += matches.length;
        } else {
          missingVariables.push(variable);
        }
      }
    } else {
      // Simple variable
      const replacement = replaceSimpleVariable(variable, data);
      const variablePattern = buildVariablePattern(variable);

      if (replacement === null) {
        missingVariables.push(variable);
      } else {
        const matches = html.match(variablePattern);
        if (matches && matches.length > 0) {
          html = html.replace(variablePattern, replacement);
          replacementCount += matches.length;
        } else {
          missingVariables.push(variable);
        }
      }
    }
  }

  return {
    html,
    missingVariables,
    replacementCount
  };
}

/**
 * Replace a simple (non-grupo) variable
 * @param variable - Variable name (without braces)
 * @param data - Report data
 * @returns Replacement value or null if not found
 */
function replaceSimpleVariable(variable: string, data: ReportData): string | null {
  // Check for radar with numeric size: radar:250, radar_geral:300, radar_segmento:200
  const radarNumericMatch = variable.match(/^(radar|radar_geral|radar_segmento):(\d+)$/);
  if (radarNumericMatch) {
    const radarType = radarNumericMatch[1];
    const size = radarNumericMatch[2];
    const componentMap: Record<string, string> = {
      radar: "RADAR",
      radar_geral: "RADAR_GERAL",
      radar_segmento: "RADAR_SEGMENTO"
    };
    const componentType = componentMap[radarType ?? ""];
    if (componentType) {
      return `{{COMPONENT:${componentType}:${size}}}`;
    }
  }

  switch (variable) {
    // EMPRESA
    case "empresa_nome": {
      return data.empresa.nomeFantasia || data.empresa.razaoSocial || "";
    }
    case "empresa_cnpj": {
      return data.empresa.cnpj || "";
    }
    case "empresa_setor": {
      return data.empresa.setores.join(", ");
    }

    // QUESTIONÁRIO/PROJETO
    case "questionario_nome": {
      return data.questionario.nome || "";
    }
    case "questionario_descricao": {
      return data.questionario.descricao || "";
    }
    case "projeto_nome": {
      return data.projeto.nome || "";
    }
    case "data_geracao": {
      return formatDate(data.dataGeracao);
    }
    case "gerado_por": {
      return data.geradoPor || "";
    }

    // RESULTADOS
    case "media_geral": {
      return formatPercentage(calculateMediaGeral(data));
    }
    case "total_perguntas": {
      return String(calculateTotalPerguntas(data));
    }
    case "total_grupos": {
      return String(data.grupos.length);
    }
    case "dimensao_maior_nota": {
      return findHighestDimension(data)?.nome || "";
    }
    case "dimensao_menor_nota": {
      return findLowestDimension(data)?.nome || "";
    }

    // COMPONENTES VISUAIS
    // These are placeholders - actual rendering happens in HTML-to-ReactPDF conversion
    // Supports size parameter: radar, radar:200, radar:pequeno, radar:medio, radar:grande
    case "radar":
    case "radar:pequeno":
    case "radar:medio":
    case "radar:grande": {
      const sizeMap: Record<string, number> = {
        radar: 350,
        "radar:pequeno": 200,
        "radar:medio": 300,
        "radar:grande": 450
      };
      const size = sizeMap[variable] ?? 350;
      return `{{COMPONENT:RADAR:${size}}}`;
    }
    case "radar_geral":
    case "radar_geral:pequeno":
    case "radar_geral:medio":
    case "radar_geral:grande": {
      const sizeMap: Record<string, number> = {
        radar_geral: 350,
        "radar_geral:pequeno": 200,
        "radar_geral:medio": 300,
        "radar_geral:grande": 450
      };
      const size = sizeMap[variable] ?? 350;
      return `{{COMPONENT:RADAR_GERAL:${size}}}`;
    }
    case "radar_segmento":
    case "radar_segmento:pequeno":
    case "radar_segmento:medio":
    case "radar_segmento:grande": {
      const sizeMap: Record<string, number> = {
        radar_segmento: 350,
        "radar_segmento:pequeno": 200,
        "radar_segmento:medio": 300,
        "radar_segmento:grande": 450
      };
      const size = sizeMap[variable] ?? 350;
      return `{{COMPONENT:RADAR_SEGMENTO:${size}}}`;
    }
    case "tabela_resultados": {
      return "{{COMPONENT:TABLE}}";
    }
    case "cabecalho": {
      return "{{COMPONENT:HEADER}}";
    }
    case "pagina": {
      return "{{COMPONENT:PAGE_NUMBER}}";
    }
    case "quebra_pagina": {
      return "{{COMPONENT:PAGE_BREAK}}";
    }

    default: {
      return null;
    }
  }
}

/**
 * Replace a grupo variable
 * @param index - Group index (1-indexed position in the available grupos array)
 * @param property - Property name (titulo, media, analise, etc)
 * @param data - Report data
 * @returns Replacement value or null if not found
 */
function replaceGrupoVariable(index: number, property: string, data: ReportData): string | null {
  // Find grupo by position in array (1-indexed)
  // Note: grupos array is already filtered (radar=true) and sorted by ordemQuestionario
  const grupo = data.grupos[index - 1];

  if (!grupo) {
    return null;
  }

  // Calculate the 0-indexed position for result lookup
  const grupoIndex = index - 1;

  switch (property) {
    case "nome":
    case "titulo": {
      return grupo.nome || "";
    }

    case "descricao": {
      return grupo.descricao || "";
    }

    case "media": {
      return formatPercentage(calculateGrupoMedia(grupo, grupoIndex, data));
    }

    case "feedback": {
      // Select feedback based on grupo's score
      const media = calculateGrupoMedia(grupo, grupoIndex, data);
      return selectFeedbackByScore(grupo, media);
    }

    case "classificacao": {
      // Return just the classification title based on score
      const mediaClassificacao = calculateGrupoMedia(grupo, grupoIndex, data);
      return selectClassificacaoByScore(grupo, mediaClassificacao);
    }

    case "badge": {
      // Return styled HTML badge for the classification
      const mediaBadge = calculateGrupoMedia(grupo, grupoIndex, data);
      return generateBadgeHtml(grupo, mediaBadge);
    }

    default: {
      return null;
    }
  }
}

/**
 * Calculate média geral across all grupos
 * @param data
 */
function calculateMediaGeral(data: ReportData): number {
  if (!data.resultados?.empresa || data.resultados.empresa.length === 0) {
    return 0;
  }

  let sum = 0;
  for (const segment of data.resultados.empresa) {
    sum += segment.value;
  }
  return sum / data.resultados.empresa.length;
}

/**
 * Calculate total number of perguntas
 * @param data
 */
function calculateTotalPerguntas(data: ReportData): number {
  // This would need to be calculated from actual pergunta data
  // For now, return estimate based on grupos
  return data.grupos.length * 5; // Assuming ~5 questions per group
}

/**
 * Calculate média for a specific grupo
 * Uses name-based lookup to guarantee correct matching regardless of array order
 * Falls back to index-based lookup only if name match fails
 * @param grupo - The grupo to get média for
 * @param grupoIndex - The index of the grupo in data.grupos array (0-indexed)
 * @param data - Report data
 */
function calculateGrupoMedia(grupo: TGruposDiagnostico, grupoIndex: number, data: ReportData): number {
  // Primary: Use name-based lookup (guaranteed correct matching)
  const segmentByName = data.resultados.empresa.find((s) => s.segment === grupo.nome);
  if (segmentByName) {
    return segmentByName.value;
  }

  // Fallback: Try index-based lookup if name doesn't match
  const segmentByIndex = data.resultados.empresa[grupoIndex];
  if (segmentByIndex !== undefined) {
    return segmentByIndex.value;
  }

  return 0;
}

/**
 * Select feedback based on grupo's score
 * Finds the feedback whose range (start-end) contains the given score
 * @param grupo - Grupo with feedbacks array
 * @param score - Score to match (0-100)
 * @returns Feedback description or empty string if no match
 */
function selectFeedbackByScore(grupo: TGruposDiagnostico, score: number): string {
  if (!grupo.feedbacks || grupo.feedbacks.length === 0) {
    return "";
  }

  // Find feedback where start <= score <= end
  const matchingFeedback = grupo.feedbacks.find((feedback) => score >= feedback.start && score <= feedback.end);

  return matchingFeedback?.description || "";
}

/**
 * Select classification title based on grupo's score
 * Returns just the title (e.g., "ESSENCIAL", "INCREMENTAL", "PROTAGONISTA")
 * @param grupo - Grupo with feedbacks array
 * @param score - Score to match (0-100)
 * @returns Classification title or empty string if no match
 */
function selectClassificacaoByScore(grupo: TGruposDiagnostico, score: number): string {
  if (!grupo.feedbacks || grupo.feedbacks.length === 0) {
    return "";
  }

  // Find feedback where start <= score <= end
  const matchingFeedback = grupo.feedbacks.find((feedback) => score >= feedback.start && score <= feedback.end);

  return matchingFeedback?.title || "";
}

/**
 * Badge color mapping based on classification title
 * Default colors inspired by the reference design
 */
const BADGE_COLORS: Record<string, { bg: string; text: string }> = {
  ESSENCIAL: { bg: "#8B2942", text: "#FFFFFF" },
  INCREMENTAL: { bg: "#4A4A4A", text: "#FFFFFF" },
  PROTAGONISTA: { bg: "#2D5A3D", text: "#FFFFFF" },
  // Fallback for other classifications
  DEFAULT: { bg: "#6B7280", text: "#FFFFFF" }
};

/**
 * Generate styled HTML badge for classification
 * @param grupo - Grupo with feedbacks array
 * @param score - Score to match (0-100)
 * @returns HTML string with styled badge
 */
function generateBadgeHtml(grupo: TGruposDiagnostico, score: number): string {
  const classificacao = selectClassificacaoByScore(grupo, score);

  if (!classificacao) {
    return "";
  }

  // Get colors for this classification (case-insensitive lookup)
  const upperClassificacao = classificacao.toUpperCase();
  const colors = BADGE_COLORS[upperClassificacao] ?? BADGE_COLORS.DEFAULT ?? { bg: "#6B7280", text: "#FFFFFF" };

  // Generate inline-styled HTML badge
  const badgeStyle = [
    `background-color: ${colors.bg}`,
    `color: ${colors.text}`,
    "padding: 4px 12px",
    "border-radius: 4px",
    "font-size: 11px",
    "font-weight: 600",
    "text-transform: uppercase",
    "letter-spacing: 0.5px",
    "display: inline-block"
  ].join("; ");

  return `<span style="${badgeStyle}">${classificacao}</span>`;
}

/**
 * Find grupo with highest score
 * @param data
 */
function findHighestDimension(data: ReportData): TGruposDiagnostico | null {
  if (!data.resultados?.empresa || data.resultados.empresa.length === 0) {
    return null;
  }

  const firstSegment = data.resultados.empresa[0];
  if (!firstSegment) {
    return null;
  }

  let highest = firstSegment;
  for (const segment of data.resultados.empresa) {
    if (segment.value > highest.value) {
      highest = segment;
    }
  }

  return data.grupos.find((g) => g.nome === highest.segment) || null;
}

/**
 * Find grupo with lowest score
 * @param data
 */
function findLowestDimension(data: ReportData): TGruposDiagnostico | null {
  if (!data.resultados?.empresa || data.resultados.empresa.length === 0) {
    return null;
  }

  const firstSegment = data.resultados.empresa[0];
  if (!firstSegment) {
    return null;
  }

  let lowest = firstSegment;
  for (const segment of data.resultados.empresa) {
    if (segment.value < lowest.value) {
      lowest = segment;
    }
  }

  return data.grupos.find((g) => g.nome === lowest.segment) || null;
}

/**
 * Format number as percentage
 * @param value
 */
function formatPercentage(value: number): string {
  return `${value.toFixed(1)}%`;
}

/**
 * Format date in pt-BR format
 * @param date
 */
function formatDate(date: Date): string {
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  }).format(date);
}

/**
 * Escape special regex characters
 * @param str
 * @param string_
 */
function escapeRegex(string_: string): string {
  return string_.replaceAll(/[$()*+.?[\\\]^{|}]/g, "\\$&");
}

/**
 * Build regex pattern for a variable with optional whitespace
 * @param variable
 */
function buildVariablePattern(variable: string): RegExp {
  return new RegExp(`\\{\\{\\s*${escapeRegex(variable)}\\s*\\}\\}`, "g");
}

/**
 * Get list of valid grupo indices from report data (1-indexed)
 * Returns [1, 2, 3, ...n] where n is the number of available grupos
 * @param data
 */
export function getAvailableGrupoIndices(data: ReportData): number[] {
  return data.grupos.map((_, index) => index + 1);
}

/**
 * @deprecated Use getAvailableGrupoIndices instead
 * Get list of all grupo orders from report data (1-indexed)
 * @param data
 */
export function getAvailableGrupoOrdens(data: ReportData): number[] {
  return getAvailableGrupoIndices(data);
}
